import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ExcelTableEditor.css';

const ExcelTableEditor = ({ 
  data = [], 
  columns = [], 
  onSave, 
  onCancel, 
  tableName = '',
  isVisible = false 
}) => {
  const [editData, setEditData] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [dragSelection, setDragSelection] = useState(null);
  const [copiedData, setCopiedData] = useState(null);
  const tableRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize edit data when component mounts or data changes
  useEffect(() => {
    if (data && data.length > 0) {
      setEditData([...data]);
    } else {
      // Create empty row if no data
      const emptyRow = {};
      columns.forEach(col => {
        emptyRow[col.name] = '';
      });
      setEditData([emptyRow]);
    }
  }, [data, columns]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  // Handle keyboard navigation and shortcuts
  const handleKeyDown = useCallback((e) => {
    if (!selectedCell) return;

    const { rowIndex, colIndex } = selectedCell;
    let newRowIndex = rowIndex;
    let newColIndex = colIndex;

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        newRowIndex = Math.max(0, rowIndex - 1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        newRowIndex = Math.min(editData.length - 1, rowIndex + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        newColIndex = Math.max(0, colIndex - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        newColIndex = Math.min(columns.length - 1, colIndex + 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (editingCell) {
          handleCellSave();
        } else {
          handleCellEdit(rowIndex, colIndex);
        }
        break;
      case 'Escape':
        e.preventDefault();
        if (editingCell) {
          handleCancelEdit();
        }
        break;
      case 'F2':
        e.preventDefault();
        handleCellEdit(rowIndex, colIndex);
        break;
      case 'Delete':
        if (!editingCell) {
          e.preventDefault();
          handleDeleteCell();
        }
        break;
      case 'c':
        if (e.ctrlKey && !editingCell) {
          e.preventDefault();
          handleCopy();
        }
        break;
      case 'v':
        if (e.ctrlKey && !editingCell) {
          e.preventDefault();
          handlePaste();
        }
        break;
      default:
        // Start editing if alphanumeric key is pressed
        if (!editingCell && e.key.length === 1 && !e.ctrlKey && !e.altKey) {
          handleCellEdit(rowIndex, colIndex, e.key);
        }
        break;
    }

    if (newRowIndex !== rowIndex || newColIndex !== colIndex) {
      setSelectedCell({ rowIndex: newRowIndex, colIndex: newColIndex });
    }
  }, [selectedCell, editingCell, editData.length, columns.length]);

  // Add keyboard event listener
  useEffect(() => {
    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown, isVisible]);

  const handleCellClick = (rowIndex, colIndex) => {
    setSelectedCell({ rowIndex, colIndex });
    if (editingCell) {
      handleCellSave();
    }
  };

  const handleCellDoubleClick = (rowIndex, colIndex) => {
    handleCellEdit(rowIndex, colIndex);
  };

  const handleCellEdit = (rowIndex, colIndex, initialValue = '') => {
    const currentValue = editData[rowIndex]?.[columns[colIndex]?.name] || '';
    setEditingCell({ rowIndex, colIndex });
    setEditValue(initialValue || currentValue);
  };

  const handleCellSave = () => {
    if (!editingCell) return;

    const { rowIndex, colIndex } = editingCell;
    const columnName = columns[colIndex]?.name;

    if (columnName) {
      const newData = [...editData];
      newData[rowIndex] = {
        ...newData[rowIndex],
        [columnName]: editValue
      };
      setEditData(newData);
    }

    setEditingCell(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleDeleteCell = () => {
    if (!selectedCell) return;

    const { rowIndex, colIndex } = selectedCell;
    const columnName = columns[colIndex]?.name;

    if (columnName) {
      const newData = [...editData];
      newData[rowIndex] = {
        ...newData[rowIndex],
        [columnName]: ''
      };
      setEditData(newData);
    }
  };

  const handleCopy = () => {
    if (!selectedCell) return;

    const { rowIndex, colIndex } = selectedCell;
    const columnName = columns[colIndex]?.name;
    const value = editData[rowIndex]?.[columnName] || '';
    
    setCopiedData({ value, type: 'single' });
    
    // Also copy to system clipboard
    navigator.clipboard?.writeText(value);
  };

  const handlePaste = () => {
    if (!selectedCell || !copiedData) return;

    const { rowIndex, colIndex } = selectedCell;
    const columnName = columns[colIndex]?.name;

    if (columnName) {
      const newData = [...editData];
      newData[rowIndex] = {
        ...newData[rowIndex],
        [columnName]: copiedData.value
      };
      setEditData(newData);
    }
  };

  const addNewRow = () => {
    const newRow = {};
    columns.forEach(col => {
      newRow[col.name] = '';
    });
    setEditData([...editData, newRow]);
  };

  const deleteRow = (rowIndex) => {
    if (editData.length > 1) {
      const newData = editData.filter((_, index) => index !== rowIndex);
      setEditData(newData);
      
      // Adjust selected cell if necessary
      if (selectedCell && selectedCell.rowIndex >= newData.length) {
        setSelectedCell({ ...selectedCell, rowIndex: newData.length - 1 });
      }
    }
  };

  const handleSave = () => {
    onSave(editData);
  };

  const formatCellValue = (value, column) => {
    if (!value && value !== 0) return '';
    
    switch (column.type) {
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'datetime':
        return new Date(value).toLocaleString();
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: column.unit || 'USD'
        }).format(value);
      case 'decimal4':
        return parseFloat(value).toFixed(4);
      case 'comma':
        return new Intl.NumberFormat().format(value);
      default:
        return value;
    }
  };

  const renderCell = (rowIndex, colIndex) => {
    const column = columns[colIndex];
    const value = editData[rowIndex]?.[column.name] || '';
    const isSelected = selectedCell?.rowIndex === rowIndex && selectedCell?.colIndex === colIndex;
    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.colIndex === colIndex;

    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleCellSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleCellSave();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              handleCancelEdit();
            }
            e.stopPropagation();
          }}
          className="excel-cell-input"
        />
      );
    }

    return (
      <div 
        className={`excel-cell-content ${isSelected ? 'selected' : ''}`}
        onClick={() => handleCellClick(rowIndex, colIndex)}
        onDoubleClick={() => handleCellDoubleClick(rowIndex, colIndex)}
      >
        {formatCellValue(value, column)}
      </div>
    );
  };

  if (!isVisible) return null;

  return (
    <div className="excel-table-editor-overlay">
      <div className="excel-table-editor">
        <div className="excel-header">
          <h3>Edit Table: {tableName}</h3>
          <div className="excel-toolbar">
            <button onClick={handleSave} className="excel-btn excel-btn-save">
              💾 Save Changes
            </button>
            <button onClick={onCancel} className="excel-btn excel-btn-cancel">
              ❌ Cancel
            </button>
          </div>
        </div>

        <div className="excel-table-container" ref={tableRef}>
          <table className="excel-table">
            <thead>
              <tr>
                <th className="excel-row-header">#</th>
                {columns.map((column, colIndex) => (
                  <th key={column.name} className="excel-column-header">
                    <div className="excel-column-title">
                      {column.label || column.name}
                      {column.unit && <span className="excel-unit">({column.unit})</span>}
                    </div>
                  </th>
                ))}
                <th className="excel-actions-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {editData.map((row, rowIndex) => (
                <tr key={rowIndex} className="excel-row">
                  <td className="excel-row-number">{rowIndex + 1}</td>
                  {columns.map((column, colIndex) => (
                    <td 
                      key={`${rowIndex}-${colIndex}`}
                      className={`excel-cell ${
                        selectedCell?.rowIndex === rowIndex && selectedCell?.colIndex === colIndex 
                          ? 'excel-cell-selected' : ''
                      }`}
                    >
                      {renderCell(rowIndex, colIndex)}
                    </td>
                  ))}
                  <td className="excel-actions-cell">
                    <button 
                      onClick={() => deleteRow(rowIndex)}
                      className="excel-btn excel-btn-delete-row"
                      disabled={editData.length === 1}
                      title="Delete Row"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="excel-footer">
          <div className="excel-footer-left">
            <button onClick={addNewRow} className="excel-btn excel-btn-add">
              ➕ Add Row
            </button>
          </div>
          <div className="excel-footer-right">
            <div className="excel-status">
              {selectedCell && (
                <span>
                  Selected: Row {selectedCell.rowIndex + 1}, Column {columns[selectedCell.colIndex]?.label || columns[selectedCell.colIndex]?.name}
                </span>
              )}
            </div>
            <div className="excel-shortcuts">
              <small>
                <strong>Shortcuts:</strong> Enter/F2: Edit | Esc: Cancel | Arrows: Navigate | Ctrl+C: Copy | Ctrl+V: Paste | Del: Clear
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcelTableEditor; 